import { type FC, type ReactNode, useCallback, useMemo, useState } from "react";

import { Modal, type Status } from "@snapchat/snap-design-system";

import { useFeatureGates } from "../../cof-feature-gates/hooks";
import { CofConfigId, SurfaceType } from "../../gql-generated/types";
import { Flexbox } from "../../shared/components";
import { type ModalProps } from "../../shared/hooks";
import {
    type LensPreviewMediaItem,
    SNAPCHAT_MAX_NUM_PREVIEWS,
    SPECTACLES_MAX_NUM_PREVIEWS,
    defaultPreviewMediaItem,
} from "../../shared/state";
import { useCropper } from "../hooks/useCropper";
import { PreviewAspectRatio, PreviewResolution, type PreviewValidationError, PreviewValidationErrors } from "../models";
import { getAcceptedFileTypes, isVideo, videoTypes } from "../validation";

import { LensPreviewWrapper, previewsModalCss } from "./LensPreview.styles";
import { LensPreviewForm } from "./LensPreviewForm";
import { lensPreviewValidationMessage } from "./LensPreviewForm.styles";
import { LensPreviewSpecs } from "./LensPreviewSpecs";
import { LensPreviewsViewer } from "./LensPreviewsViewer";
import { MediaCropper } from "./MediaCropper";
import { PreviewInReviewComponent } from "./PreviewInReviewComponent";
import { PreviewValidationMessage } from "./PreviewValidationMessage";
import { SortablePreviewList } from "./SortablePreviewList";
import {
    LensPreviewChangeCta,
    LensPreviewUploadCta,
    LensPreviewUploadCtaSpectacles,
    LensPreviewUploadVideoCta,
    SpectaclesVideoPreviewError,
    messages,
} from "./messages.generated";

const GUIDE_URL = "https://docs.snap.com/lens-studio/references/guides/publishing/configuring/creating-a-preview-video";
const SPECTACLES_GUIDE_URL = "https://docs.snap.com/spectacles/get-started/start-buiding/publishing-lens";

const aspectRatios: Record<SurfaceType, PreviewAspectRatio> = {
    [SurfaceType.SurfaceTypeSpectacles]: PreviewAspectRatio.threeFour,
    [SurfaceType.SurfaceTypeSnapchat]: PreviewAspectRatio.nineSixteen,
    [SurfaceType.SurfaceTypeUnset]: PreviewAspectRatio.nineSixteen,
} as const;

const resolution = {
    [SurfaceType.SurfaceTypeSnapchat]: PreviewResolution.minNineSixteen,
    [SurfaceType.SurfaceTypeSpectacles]: PreviewResolution.minThreeFour,
    [SurfaceType.SurfaceTypeUnset]: PreviewResolution.minNineSixteen,
} as const;

const guideUrl = {
    [SurfaceType.SurfaceTypeSnapchat]: GUIDE_URL,
    [SurfaceType.SurfaceTypeSpectacles]: SPECTACLES_GUIDE_URL,
    [SurfaceType.SurfaceTypeUnset]: GUIDE_URL,
} as const;

interface LensPreviewProps {
    modalProps: ModalProps;
    previewMediaItems: LensPreviewMediaItem[];
    validationMessage: string | null;
    uploadFile: (file: File, idx: number, aspectRatio?: PreviewAspectRatio) => Promise<void>;
    removePreview: (idx: number) => void;
    movePreview: (oldIdx: number, newIdx: number) => void;
    finalizeChanges: () => void;
    showModal: () => void;
    previewInReview?: boolean;
    logoSrc?: string;
    isReadOnly?: boolean;
    uploadFileLoading?: boolean;
    badgeText?: ReactNode;
    badgeStatus?: Status;
    disabled?: boolean;
    shouldShowUpdateMessage?: boolean;
    isLargePreview?: boolean;
    surfaceType: readonly SurfaceType[];
    showSpectaclesPreview?: boolean;
    isExclusive?: boolean;
    videoOnly?: boolean;
    videoDurationSpec?: ReactNode;
}

export const LensPreview: FC<LensPreviewProps> = ({
    modalProps,
    previewMediaItems,
    validationMessage,
    uploadFile,
    removePreview,
    movePreview,
    finalizeChanges,
    showModal,
    previewInReview,
    isReadOnly,
    logoSrc,
    uploadFileLoading = false,
    badgeText,
    badgeStatus,
    disabled,
    shouldShowUpdateMessage = false,
    surfaceType,
    isExclusive,
    videoOnly = false,
    videoDurationSpec,
}) => {
    const { configs } = useFeatureGates();
    const isSpectaclesSubmissionEnabled = configs[CofConfigId.MyLensesSpectaclesSubmission].enabled;
    const multiPreviewsEnabled = configs[CofConfigId.MyLensesSpectaclesRnTagsMultiPreviews].enabled;

    const sortedSurfaceType: SurfaceType[] = useMemo(() => {
        return isSpectaclesSubmissionEnabled && surfaceType.length > 0
            ? [...surfaceType].sort()
            : [SurfaceType.SurfaceTypeSnapchat];
    }, [isSpectaclesSubmissionEnabled, surfaceType]);

    // State for managing cropping workflow
    const [showCropper, setShowCropper] = useState(false);
    const [currentCropItem, setCurrentCropItem] = useState<LensPreviewMediaItem | null>(null);
    const [currentCropIndex, setCurrentCropIndex] = useState<number>(0);
    const [error, setError] = useState<string | null>(null);

    const acceptFormats = videoOnly ? videoTypes.join(",") : getAcceptedFileTypes();
    const uploadCta = videoOnly ? <LensPreviewUploadVideoCta /> : <LensPreviewUploadCta />;
    const isSpectaclesOnly = surfaceType.length === 1 && surfaceType.includes(SurfaceType.SurfaceTypeSpectacles);
    const hasSpectacles = surfaceType.includes(SurfaceType.SurfaceTypeSpectacles);

    const maxNumPreviews = useMemo(() => {
        if (hasSpectacles && multiPreviewsEnabled) {
            return SPECTACLES_MAX_NUM_PREVIEWS;
        }
        return SNAPCHAT_MAX_NUM_PREVIEWS;
    }, [hasSpectacles, multiPreviewsEnabled]);

    const firstSurfaceType = sortedSurfaceType[0] ?? SurfaceType.SurfaceTypeSnapchat;

    const { handleCrop, loading: cropLoading } = useCropper(
        currentCropItem ?? defaultPreviewMediaItem,
        aspectRatios[firstSurfaceType],
    );

    const isAspectRatioError = useCallback((error: Error) => {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- error type is expected
        const errorMessage = (error as PreviewValidationError).message as PreviewValidationErrors;
        return (
            PreviewValidationErrors.VideoIncorrectAspectRatio === errorMessage ||
            PreviewValidationErrors.ImageIncorrectAspectRatio === errorMessage
        );
    }, []);

    const handleFileUpload = useCallback(
        async (file: File, idx: number) => {
            setError(null);
            try {
                await uploadFile(file, idx, aspectRatios[firstSurfaceType]);
            } catch (error) {
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- error type is expected
                if (isAspectRatioError(error as Error) || (isSpectaclesOnly && file.type.includes("video"))) {
                    setShowCropper(true);
                    setCurrentCropItem({
                        type: "default" as const,
                        isVideo: isVideo(file.type),
                        url: URL.createObjectURL(file),
                        name: file.name,
                        file: file,
                    });
                    setCurrentCropIndex(idx);
                } else {
                    setShowCropper(false);
                    setError("Failed to upload file.");
                }
            }
        },
        [uploadFile, firstSurfaceType, isAspectRatioError, isSpectaclesOnly],
    );

    const handleCropApply = useCallback(async () => {
        if (!currentCropItem) return;

        setError(null);
        try {
            const croppedFile = await handleCrop();
            if (croppedFile) {
                await uploadFile(croppedFile, currentCropIndex, aspectRatios[firstSurfaceType]);
            }

            // Reset cropping state and go back to upload interface
            setShowCropper(false);
            setCurrentCropItem(null);
            setCurrentCropIndex(0);
        } catch (cropError) {
            console.error(cropError);
            setError("Failed to process media");
        }
    }, [currentCropItem, handleCrop, uploadFile, currentCropIndex, firstSurfaceType]);

    const handleCropCancel = useCallback(() => {
        setShowCropper(false);
        setCurrentCropItem(null);
        setCurrentCropIndex(0);
        setError(null);
    }, []);

    const handleEditPreview = useCallback((preview: LensPreviewMediaItem, idx: number) => {
        setError(null);
        setShowCropper(true);
        setCurrentCropItem(preview);
        setCurrentCropIndex(idx);
    }, []);

    const handleModalCancel = useCallback(() => {
        setShowCropper(false);
        setCurrentCropItem(null);
        setCurrentCropIndex(0);
        setError(null);
        modalProps.onCancel();
    }, [modalProps]);

    const handleModalApply = useCallback(() => {
        finalizeChanges();
    }, [finalizeChanges]);

    return (
        <LensPreviewWrapper>
            {previewInReview ? (
                <PreviewInReviewComponent
                    aspectRatio={
                        isSpectaclesOnly
                            ? aspectRatios[SurfaceType.SurfaceTypeSpectacles]
                            : aspectRatios[SurfaceType.SurfaceTypeSnapchat]
                    }
                />
            ) : (
                <>
                    <LensPreviewsViewer
                        previewMedia={previewMediaItems}
                        buttonText={<LensPreviewChangeCta />}
                        buttonAction={showModal}
                        surface={firstSurfaceType}
                        autoplay={!isSpectaclesOnly}
                        badgeText={badgeText}
                        badgeStatus={badgeStatus}
                        logoSrc={logoSrc}
                        shouldShowUpdateMessage={shouldShowUpdateMessage}
                        aspectRatio={aspectRatios[firstSurfaceType]}
                        hideActionButton={Boolean(logoSrc)}
                        disabled={disabled}
                        isReadOnly={isReadOnly}
                        maxNumPreviews={maxNumPreviews}
                        showModal={showModal}
                        isExclusive={isExclusive}
                    />
                </>
            )}

            <Modal
                {...modalProps}
                footer={false}
                padBody={false}
                width="auto"
                className={previewsModalCss}
                onCancel={handleModalCancel}
            >
                {showCropper ? (
                    <LensPreviewForm
                        onApply={() => void handleCropApply()}
                        onCancel={handleCropCancel}
                        isApplyDisabled={cropLoading || uploadFileLoading}
                        isCancelDisabled={false}
                        isFormPending={cropLoading || uploadFileLoading}
                    >
                        <MediaCropper
                            isVideo={currentCropItem?.isVideo}
                            src={currentCropItem?.url ?? ""}
                            aspectRatio={aspectRatios[firstSurfaceType]}
                            error={
                                isSpectaclesOnly && currentCropItem?.isVideo ? <SpectaclesVideoPreviewError /> : error
                            }
                        />
                    </LensPreviewForm>
                ) : (
                    <LensPreviewForm
                        onApply={() => {
                            handleModalApply();
                        }}
                        onCancel={handleModalCancel}
                        isApplyDisabled={uploadFileLoading}
                        isCancelDisabled={false}
                        isFormPending={uploadFileLoading}
                    >
                        <Flexbox flex="1" gap="24px" alignItems="flex-start">
                            <LensPreviewSpecs
                                resolution={resolution[firstSurfaceType]}
                                aspectRatio={aspectRatios[firstSurfaceType]}
                                acceptsVideo={!isSpectaclesOnly}
                                acceptsImages={!videoOnly}
                                guideUrl={guideUrl[firstSurfaceType]}
                                videoDurationSpec={videoDurationSpec}
                            />
                            <SortablePreviewList
                                previewMediaItems={previewMediaItems}
                                removePreview={removePreview}
                                movePreview={movePreview}
                                editPreview={handleEditPreview}
                                uploadFile={handleFileUpload}
                                aspectRatio={aspectRatios[firstSurfaceType]}
                                acceptFormats={acceptFormats}
                                maxNumPreviews={maxNumPreviews}
                                uploadCta={isSpectaclesOnly ? <LensPreviewUploadCtaSpectacles /> : uploadCta}
                            />
                        </Flexbox>
                        <PreviewValidationMessage
                            className={lensPreviewValidationMessage}
                            validationMessage={
                                isSpectaclesOnly && previewMediaItems.some((item) => item.isVideo)
                                    ? messages.spectaclesModalVideoError.defaultMessage
                                    : validationMessage
                            }
                        />
                    </LensPreviewForm>
                )}
            </Modal>
        </LensPreviewWrapper>
    );
};
